import React from "react";

import { Button, Col, DatePicker, Divider, Form, Input, Row, Select } from "antd";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import slice from "../../reducer";

import dayjs from "dayjs";
import {
  makeSelectBaseCompanyCurrency,
  makeSelectCurrencies,
  makeSelectTransportCharacteristics,
  makeSelectUserInfo,
} from "../../../../containers/App/selectors";
import LocationModel from "../../../../viewModels/Location/LocationModel";

import CustomNumericFormat from "../../../../components/CustomNumericFormat";
import LocationsFormItems from "../../FormItems/LocationsFormItems";

export type CreateFtlApplicationInputModel = {
  fromLocation: LocationModel;
  waypointLocationId?: number;
  toLocation: LocationModel;
  fromDate?: dayjs.Dayjs;
  count: number;
  cost?: number;
  currencyId: number;
  transportCharacteristicId: number;
  companyId: number;
};

const FtlForm = React.memo(() => {
  const { t } = useTranslation("translations");

  const dispatch = useDispatch();

  const baseCompanyCurrency = useSelector(makeSelectBaseCompanyCurrency());

  const transportCharacteristics = useSelector(makeSelectTransportCharacteristics());
  const transportCharacteristic = transportCharacteristics.find((x) => x.loadType === "FTL")!;
  const currencies = useSelector(makeSelectCurrencies());

  const userInfo = useSelector(makeSelectUserInfo());

  const [form] = Form.useForm();

  const handleFinish = (model: CreateFtlApplicationInputModel) => {
    dispatch(slice.actions.createFtlApplication(model));
  };

  const handleReset = React.useCallback(() => {
    form.resetFields();
  }, [form]);

  return (
    <Form
      form={form}
      onFinish={handleFinish}
      initialValues={{
        transportCharacteristicId: transportCharacteristic.id,
        currencyId: baseCompanyCurrency.id,
        count: 1,
        companyId: userInfo.company.id,
      }}
      layout="vertical"
      clearOnDestroy
    >
      <Form.Item name="transportCharacteristicId" noStyle>
        <Input hidden />
      </Form.Item>
      <Form.Item name="companyId" noStyle>
        <Input hidden />
      </Form.Item>

      <Divider>{t("route_parameters")}</Divider>

      <LocationsFormItems openWaypointLocation={false} />
      <Form.Item name="fromDate" label={t("choose_date_of_ready_cargo")}>
        <DatePicker placeholder="" style={{ width: "100%" }} format="DD MMMM YYYY" />
      </Form.Item>

      <Divider>{t("cargo_parameters")}</Divider>

      <Row gutter={8}>
        <Col span={8}>
          <Form.Item name="count" rules={[{ required: true, message: t("enter_count_ftl_trucks") }]} label={t("count_ftl_trucks")}>
            <CustomNumericFormat allowNegative={false} decimalScale={0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="cost" label={t("cargo_cost")}>
            <CustomNumericFormat allowNegative={false} decimalScale={2} thousandSeparator=" " />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="currencyId" label={t("currency")}>
            <Select options={currencies.map((x) => ({ label: x.name, value: x.id }))} />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Row justify="end" gutter={8}>
        <Col span={6}>
          <Button style={{ width: "100%" }} onClick={handleReset}>
            {t("reset")}
          </Button>
        </Col>
        <Col span={6}>
          <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
            {t("calculate")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
});

export default FtlForm;
